import { resolveHotelProfilePackageBookingSsot } from "../agentHotelProfilePricing";
import { getHotelProfilePackageSupplierUsd } from "../hotelSellingPrice";
import { roundMoney2 } from "../../pricing/pricingEngine";
import {
  buildAgentHotelProfilePackageMultiRoomPricingDisplay,
  isHotelProfileMultiRoomComboPackage,
} from "../pricing/resolveAgentHotelMultiRoomPricingDisplay";

const CUSTOMER_FACING_ILS = "ILS";
const PRICE_MATCH_TOLERANCE = 0.02;

function pricesNearlyEqual(a, b) {
  const left = Number(a);
  const right = Number(b);
  if (!Number.isFinite(left) || !Number.isFinite(right)) return false;
  const tol = Math.max(PRICE_MATCH_TOLERANCE, Math.abs(left) * 0.0001);
  return Math.abs(left - right) <= tol;
}

/**
 * Baseline/modal old price must never be raw supplier USD or displayCurrencies.ils alone.
 */
export function isDisallowedBaselinePriceCandidate(value, pkg = null) {
  if (value == null || !Number.isFinite(Number(value))) return true;
  const n = Number(value);

  const supplier = getHotelProfilePackageSupplierUsd(pkg);
  if (supplier != null && Number.isFinite(Number(supplier))) {
    const supplierNum = Number(supplier);
    if (pricesNearlyEqual(n, supplierNum)) return true;
    // Reject rounded supplier USD shown as pseudo "display" (e.g. 277 vs supplier 276).
    const supplierSlack = Math.max(2, Math.abs(supplierNum) * 0.02);
    if (Math.abs(n - supplierNum) <= supplierSlack) return true;
  }

  const displayIls =
    pkg?.displayCurrencies?.ils ??
    pkg?.selectedPriceSnapshot?.displayCurrencies?.ils ??
    pkg?.selectedPriceSnapshot?.pricingSnapshot?.displayCurrencies?.ils ??
    pkg?.pricingSnapshot?.displayCurrencies?.ils ??
    null;
  if (displayIls != null && pricesNearlyEqual(n, Number(displayIls))) {
    return true;
  }

  return false;
}

export function resolveStoredCustomerFacingIlsFromPackageOrDraft({
  pkg = null,
  bookingSsotIls = null,
  previousVisibleCustomerFacingPrice = null,
} = {}) {
  const candidates = [
    previousVisibleCustomerFacingPrice,
    bookingSsotIls,
    pkg?.bookingSsotIls,
    pkg?.selectedPriceSnapshot?.finalPriceIls,
    pkg?.selectedPriceSnapshot?.bookingSsotIls,
  ];

  for (const raw of candidates) {
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0 && !isDisallowedBaselinePriceCandidate(n, pkg)) {
      return roundMoney2(n);
    }
  }

  return null;
}

/**
 * Minimal serializable snapshot for final-check recalculation on the draft.
 */
export function buildDraftProfilePriceContextSnapshot(profilePriceContext = null) {
  if (!profilePriceContext || typeof profilePriceContext !== "object") return null;
  return {
    selectedHotelCurrency: profilePriceContext.selectedHotelCurrency ?? null,
    hotelSearchListDisplayCurrency:
      profilePriceContext.hotelSearchListDisplayCurrency ?? null,
    hotelId: profilePriceContext.hotelId ?? null,
    agentHotelPricing: profilePriceContext.agentHotelPricing ?? null,
    currencyRate: profilePriceContext.currencyRate ?? null,
    adminUsdRate:
      profilePriceContext.adminUsdRate ?? profilePriceContext.USDrate ?? null,
    adminEurRate:
      profilePriceContext.adminEurRate ?? profilePriceContext.EURrate ?? null,
    USDrate: profilePriceContext.USDrate ?? profilePriceContext.adminUsdRate ?? null,
    EURrate: profilePriceContext.EURrate ?? profilePriceContext.adminEurRate ?? null,
  };
}

/**
 * Recalculated customer-facing ILS after live row merge — never supplier USD.
 * @returns {{ total: number|null, sourceUsed: string }}
 */
export function resolveHotelFinalCheckRecalculatedCustomerFacingIls({
  pkg,
  room = null,
  profilePriceContext = null,
}) {
  if (isHotelProfileMultiRoomComboPackage(room, pkg)) {
    const display = buildAgentHotelProfilePackageMultiRoomPricingDisplay({
      room,
      pkg,
      profilePriceContext,
    });
    if (display.isMultiRoom && display.totalIls != null) {
      return {
        total: display.totalIls,
        sourceUsed: "recalculatedCustomerFacingPrice",
      };
    }
  }

  const ssot = resolveHotelProfilePackageBookingSsot(pkg, profilePriceContext, {
    roomId: room?.id ?? room?.Id ?? null,
  });
  if (ssot.bookingSsotIls != null && Number.isFinite(Number(ssot.bookingSsotIls))) {
    return {
      total: roundMoney2(Number(ssot.bookingSsotIls)),
      sourceUsed: "recalculatedCustomerFacingPrice",
    };
  }

  return { total: null, sourceUsed: "missing" };
}

/**
 * Modal baseline — displayedPriceAtClick or stored booking SSOT only (customer-facing ILS).
 * @returns {{ total: number|null, sourceUsed: string }}
 */
export function resolveHotelFinalCheckBaselineCustomerFacingIls({
  pkg,
  room = null,
  profilePriceContext = null,
  displayedPriceAtClick = null,
  previousVisibleCustomerFacingPrice = null,
  bookingSsotIls = null,
}) {
  if (
    displayedPriceAtClick != null &&
    Number.isFinite(Number(displayedPriceAtClick)) &&
    !isDisallowedBaselinePriceCandidate(displayedPriceAtClick, pkg)
  ) {
    return {
      total: roundMoney2(Number(displayedPriceAtClick)),
      sourceUsed: "displayedPriceAtClick",
    };
  }

  const stored = resolveStoredCustomerFacingIlsFromPackageOrDraft({
    pkg,
    bookingSsotIls,
    previousVisibleCustomerFacingPrice,
  });
  if (stored != null) {
    return {
      total: stored,
      sourceUsed: "previousVisibleCustomerFacingPrice",
    };
  }

  return resolveHotelFinalCheckRecalculatedCustomerFacingIls({
    pkg,
    room,
    profilePriceContext,
  });
}

/**
 * Customer-facing combo total — baseline when explicit, otherwise recalc SSOT.
 * @returns {{ total: number|null, sourceUsed: string }}
 */
export function resolveHotelFinalCheckComboCustomerFacingTotal({
  pkg,
  room = null,
  profilePriceContext = null,
  displayedPriceAtClick = null,
  previousVisibleComboTotal = null,
  bookingSsotIls = null,
}) {
  return resolveHotelFinalCheckBaselineCustomerFacingIls({
    pkg,
    room,
    profilePriceContext,
    displayedPriceAtClick,
    previousVisibleCustomerFacingPrice: previousVisibleComboTotal,
    bookingSsotIls,
  });
}

/** Visible combo total at room-select click — same SSOT as the profile room card. */
export function resolveHotelProfileDisplayedComboTotalAtClick({
  room,
  pkg,
  profilePriceContext = null,
}) {
  return resolveHotelFinalCheckBaselineCustomerFacingIls({
    room,
    pkg,
    profilePriceContext,
    bookingSsotIls:
      pkg?.selectedPriceSnapshot?.finalPriceIls ??
      pkg?.bookingSsotIls ??
      null,
    previousVisibleCustomerFacingPrice:
      pkg?.selectedPriceSnapshot?.finalPriceIls ??
      pkg?.bookingSsotIls ??
      null,
  });
}

export function mergeLiveHotelPackageRowForFinalCheckRecalc(beforePkg, liveRow) {
  if (!beforePkg) return liveRow ?? null;
  if (!liveRow) return beforePkg;
  return {
    ...beforePkg,
    ...liveRow,
    PackagePrice: liveRow.PackagePrice ?? beforePkg.PackagePrice,
    Rooms: liveRow.Rooms ?? beforePkg.Rooms,
    SimplePrice: liveRow.SimplePrice ?? beforePkg.SimplePrice,
  };
}

export const HOTEL_FINAL_CHECK_CUSTOMER_FACING_CURRENCY = CUSTOMER_FACING_ILS;
